import React, { useId } from 'react';
import { ChevronDown, ChevronUp, Wind } from 'lucide-react';

/**
 * Phase 19 F13 (P1-6): equipment settings on the nursing plan of care. HHSC's PDN clarification
 * effective 2025-11-01 requires the settings or modes of required equipment (e.g. ventilator,
 * oxygen) on the plan of care; these values print on the CMS-485 and F00028.
 */
export interface EquipmentSettings {
  vent: { model: string; mode: string; rate: string; tidalVolumeMl: string; pip: string; peep: string; fio2: string; pressureSupport: string; backupVentInHome: boolean };
  oxygen: { flowLpm: string; delivery: string; use: '' | 'CONTINUOUS' | 'PRN' | 'SLEEP'; spo2Target: string };
  trach: { type: string; size: string; cuff: '' | 'CUFFED' | 'CUFFLESS' | 'TTS'; changeFrequency: string };
  suction: { type: string; catheterSize: string };
  coughAssist: { settings: string };
  feedingPump: { route: string; formula: string; rateMlPerHour: string; volumeMl: string; schedule: string };
}

export const emptyEquipmentSettings = (): EquipmentSettings => ({
  vent: { model: '', mode: '', rate: '', tidalVolumeMl: '', pip: '', peep: '', fio2: '', pressureSupport: '', backupVentInHome: false },
  oxygen: { flowLpm: '', delivery: '', use: '', spo2Target: '' },
  trach: { type: '', size: '', cuff: '', changeFrequency: '' },
  suction: { type: '', catheterSize: '' },
  coughAssist: { settings: '' },
  feedingPump: { route: '', formula: '', rateMlPerHour: '', volumeMl: '', schedule: '' },
});

/** A saved value merged over the empty shape (older plans have none). */
export function normalizeEquipmentSettings(value: unknown): EquipmentSettings {
  const base = emptyEquipmentSettings();
  if (!value || typeof value !== 'object') return base;
  const v = value as Partial<Record<keyof EquipmentSettings, Record<string, unknown>>>;
  return {
    vent: { ...base.vent, ...(v.vent || {}) } as EquipmentSettings['vent'],
    oxygen: { ...base.oxygen, ...(v.oxygen || {}) } as EquipmentSettings['oxygen'],
    trach: { ...base.trach, ...(v.trach || {}) } as EquipmentSettings['trach'],
    suction: { ...base.suction, ...(v.suction || {}) } as EquipmentSettings['suction'],
    coughAssist: { ...base.coughAssist, ...(v.coughAssist || {}) } as EquipmentSettings['coughAssist'],
    feedingPump: { ...base.feedingPump, ...(v.feedingPump || {}) } as EquipmentSettings['feedingPump'],
  };
}

/** Submit check: a ventilator plan needs the vent mode; an oxygen plan needs a flow. */
export function equipmentSettingsErrors(settings: EquipmentSettings, { hasVentPlan, hasOxygenPlan }: { hasVentPlan: boolean; hasOxygenPlan: boolean }): Record<string, string> {
  const errors: Record<string, string> = {};
  if (hasVentPlan && !settings.vent.mode.trim()) errors['equipmentSettings.vent.mode'] = 'Ventilator mode is required on the plan of care';
  if (hasOxygenPlan && !settings.oxygen.flowLpm.trim()) errors['equipmentSettings.oxygen.flowLpm'] = 'Oxygen flow is required on the plan of care';
  return errors;
}

type Group = keyof EquipmentSettings;
interface FieldDef { group: Group; key: string; label: string; options?: Array<[string, string]>; checkbox?: boolean; placeholder?: string }

const FIELDS: Array<{ title: string; fields: FieldDef[] }> = [
  { title: 'Ventilator', fields: [
    { group: 'vent', key: 'model', label: 'Model' },
    { group: 'vent', key: 'mode', label: 'Mode', placeholder: 'e.g., SIMV-PC, AC-VC, CPAP' },
    { group: 'vent', key: 'rate', label: 'Rate (bpm)' },
    { group: 'vent', key: 'tidalVolumeMl', label: 'Tidal volume (mL)' },
    { group: 'vent', key: 'pip', label: 'PIP (cm H2O)' },
    { group: 'vent', key: 'peep', label: 'PEEP (cm H2O)' },
    { group: 'vent', key: 'fio2', label: 'FiO2 (%)' },
    { group: 'vent', key: 'pressureSupport', label: 'Pressure support (cm H2O)' },
    { group: 'vent', key: 'backupVentInHome', label: 'Backup ventilator in the home', checkbox: true },
  ] },
  { title: 'Oxygen', fields: [
    { group: 'oxygen', key: 'flowLpm', label: 'Flow (L/min)' },
    { group: 'oxygen', key: 'delivery', label: 'Delivery', placeholder: 'e.g., via vent, trach collar, nasal cannula' },
    { group: 'oxygen', key: 'use', label: 'Use', options: [['', 'Select'], ['CONTINUOUS', 'Continuous'], ['PRN', 'As needed'], ['SLEEP', 'During sleep']] },
    { group: 'oxygen', key: 'spo2Target', label: 'SpO2 target', placeholder: 'e.g., 92% or above' },
  ] },
  { title: 'Tracheostomy', fields: [
    { group: 'trach', key: 'type', label: 'Type / brand' },
    { group: 'trach', key: 'size', label: 'Size' },
    { group: 'trach', key: 'cuff', label: 'Cuff', options: [['', 'Select'], ['CUFFED', 'Cuffed'], ['CUFFLESS', 'Cuffless'], ['TTS', 'Tight-to-shaft']] },
    { group: 'trach', key: 'changeFrequency', label: 'Change frequency' },
  ] },
  { title: 'Suction and airway clearance', fields: [
    { group: 'suction', key: 'type', label: 'Suction type', placeholder: 'e.g., inline, open, oral' },
    { group: 'suction', key: 'catheterSize', label: 'Catheter size' },
    { group: 'coughAssist', key: 'settings', label: 'Cough assist settings' },
  ] },
  { title: 'Feeding pump', fields: [
    { group: 'feedingPump', key: 'route', label: 'Route', placeholder: 'e.g., G-tube, GJ, NG' },
    { group: 'feedingPump', key: 'formula', label: 'Formula' },
    { group: 'feedingPump', key: 'rateMlPerHour', label: 'Rate (mL/h)' },
    { group: 'feedingPump', key: 'volumeMl', label: 'Volume (mL)' },
    { group: 'feedingPump', key: 'schedule', label: 'Schedule' },
  ] },
];

interface Props {
  value: EquipmentSettings;
  onChange: (value: EquipmentSettings) => void;
  errors?: Record<string, string>;
  readOnly?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
}

const EquipmentSettingsSection: React.FC<Props> = ({ value, onChange, errors = {}, readOnly = false, collapsed = false, onToggle }) => {
  const baseId = useId();
  const set = (group: Group, key: string, v: string | boolean) => onChange({ ...value, [group]: { ...value[group], [key]: v } });
  return (
    <div className="bg-sky-50 rounded-lg border border-sky-200">
      <button type="button" className="w-full p-4 flex justify-between items-center text-left" onClick={onToggle} aria-expanded={!collapsed}>
        <span className="flex items-center gap-2 text-md font-medium text-sky-900"><Wind size={18} aria-hidden="true" /> Equipment settings (required on the plan of care)</span>
        {collapsed ? <ChevronDown size={20} className="text-sky-700" aria-hidden="true" /> : <ChevronUp size={20} className="text-sky-700" aria-hidden="true" />}
      </button>
      {!collapsed && (
        <div className="p-4 border-t border-sky-200 space-y-4">
          {FIELDS.map(group => (
            <fieldset key={group.title}>
              <legend className="text-sm font-semibold text-gray-800 mb-2">{group.title}</legend>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {group.fields.map(f => {
                  const id = `${baseId}-${f.group}-${f.key}`;
                  const errorKey = `equipmentSettings.${f.group}.${f.key}`;
                  const current = (value[f.group] as Record<string, string | boolean>)[f.key];
                  if (f.checkbox) {
                    return (
                      <label key={id} htmlFor={id} className="flex items-center gap-2 text-sm text-gray-700 min-h-[44px]">
                        <input id={id} type="checkbox" disabled={readOnly} checked={Boolean(current)} onChange={e => set(f.group, f.key, e.target.checked)} />
                        {f.label}
                      </label>
                    );
                  }
                  const cls = `w-full px-3 py-2 border rounded-md ${errors[errorKey] ? 'border-red-500' : 'border-gray-300'} ${readOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`;
                  return (
                    <div key={id}>
                      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                      {f.options ? (
                        <select id={id} disabled={readOnly} value={String(current || '')} onChange={e => set(f.group, f.key, e.target.value)} className={cls}>
                          {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      ) : (
                        <input id={id} type="text" disabled={readOnly} value={String(current || '')} placeholder={f.placeholder}
                          onChange={e => set(f.group, f.key, e.target.value)} className={cls} aria-invalid={Boolean(errors[errorKey])} />
                      )}
                      {errors[errorKey] && <p className="text-red-600 text-sm mt-1">{errors[errorKey]}</p>}
                    </div>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>
      )}
    </div>
  );
};

export default EquipmentSettingsSection;
